import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Form, Button, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getSymbolsByType, updateSymbolStatus, fetchSymbolsFromBinance } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const CryptoSymbols = () => {
    const [symbols, setSymbols] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const fetchSymbols = async () => {
        try {
            setLoading(true);
            const response = await getSymbolsByType('crypto');
            
            if (response.success) {
                setSymbols(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to fetch symbols');
            }
        } catch (error) {
            console.error('Error fetching crypto symbols:', error);
            setError(error.message || 'Failed to fetch symbols');
            setSymbols([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSymbols();
    }, []);

    const handleStatusToggle = async (symbolId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        if (!window.confirm(`Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} this symbol?`)) return;
        
        try {
            await updateSymbolStatus(symbolId, newStatus);
            alert('Symbol status updated successfully');
            fetchSymbols();
        } catch (error) {
            alert(error.message || 'Failed to update symbol status');
        }
    };

    const handleImport = async () => {
        if (!window.confirm('Fetch symbols from Binance? This may take a few moments.')) return;
        
        try {
            setImporting(true);
            await fetchSymbolsFromBinance();
            alert('Symbols fetched successfully');
            fetchSymbols();
        } catch (error) {
            alert(error.message || 'Failed to fetch symbols');
        } finally {
            setImporting(false);
        }
    };

    const filteredSymbols = symbols.filter(symbol => {
        const matchesSearch = !searchTerm || 
            symbol.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            symbol.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || symbol.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Crypto Symbols" sidebutton={true} />
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <Card.Title>Crypto Symbols</Card.Title>
                            <div>
                                <Button 
                                    variant="success" 
                                    className="me-2"
                                    onClick={handleImport}
                                    disabled={importing}
                                >
                                    {importing ? 'Fetching...' : 'Fetch from Binance'}
                                </Button>
                                <Link to="/admin/add-crypto-symbol" className="btn btn-primary">
                                    Add Symbol
                                </Link>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search symbols..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <div className="table-responsive">
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Display Name</th>
                                            <th>Base Asset</th>
                                            <th>Quote Asset</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSymbols.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center">No symbols found</td>
                                            </tr>
                                        ) : (
                                            filteredSymbols.map((symbol) => (
                                                <tr key={symbol._id}>
                                                    <td>{symbol.name || '-'}</td>
                                                    <td>{symbol.displayName || '-'}</td>
                                                    <td>{symbol.baseAsset || '-'}</td>
                                                    <td>{symbol.quoteAsset || '-'}</td>
                                                    <td>
                                                        <Badge bg={symbol.status === 'active' ? 'success' : 'secondary'}>
                                                            {symbol.status || '-'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant={symbol.status === 'active' ? 'warning' : 'success'}
                                                            size="sm"
                                                            onClick={() => handleStatusToggle(symbol._id, symbol.status)}
                                                        >
                                                            {symbol.status === 'active' ? 'Deactivate' : 'Activate'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default CryptoSymbols;

